import React, { FC, useEffect } from 'react';
import { LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { Grid, TextField } from '@mui/material';
import { InvoiceDelivery, InvoicingType } from 'models/invoice';
import AddressForm from 'components/shared/address/address-form.component';
import { ClientDirInvoiceContentFormData } from 'models/forms';
import { useFormik } from 'formik';
import { Address } from 'models/contact';

type InvoiceContentFormProps = {
    labClient: LabClient;
    clinicForcedInvoicingType?: InvoicingType;
    clinicForcedInvoiceDelivery?: InvoiceDelivery;
    onChange: (formData: ClientDirInvoiceContentFormData, isFormValid: boolean) => void;
};
const InvoiceContentForm: FC<InvoiceContentFormProps> = ({
    labClient,
    clinicForcedInvoicingType,
    clinicForcedInvoiceDelivery,
    onChange,
}: InvoiceContentFormProps) => {
    const { t: translate } = useTranslation(['organization', 'client-directory', 'organization-numbers']);

    const validate = (formData: ClientDirInvoiceContentFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.invoiceAddress) {
            errors.address = 'ADDRESS_IS_REQUIRED';
        }
        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    const initialValues = () => {
        return {
            supplierReference: labClient.invoiceSettings?.supplierReference || '',
            invoiceAddress:
                labClient.invoiceSettings?.invoiceAddress || labClient.customer?.contact?.address || undefined,
        };
    };

    const formik = useFormik<ClientDirInvoiceContentFormData>({
        initialValues: initialValues(),
        validate,
        onSubmit: async (formData: ClientDirInvoiceContentFormData) => {
            onChange(formData, formik.isValid);
        },
    });

    useEffect(() => {
        formik.setValues(initialValues());
    }, [labClient]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('invoiceAddress', address);
    };

    return (
        <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
                <TextField
                    fullWidth
                    disabled={!!clinicForcedInvoicingType || !!clinicForcedInvoiceDelivery}
                    label={translate('organization:ORGANIZATION_OFFICIAL_NAME')}
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    value={labClient.customer?.officialName}
                />
            </Grid>

            <Grid item sm={4} xs={12}>
                <TextField
                    fullWidth
                    disabled={!!clinicForcedInvoicingType || !!clinicForcedInvoiceDelivery}
                    label={translate(
                        `organization-numbers:${labClient.invoiceSettings?.invoiceAddress?.country || 'SE'}_ORGANIZATION_NUMBER`,
                    )}
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    value={labClient.customer?.organizationNumber || ' '}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    label={translate('client-directory:COST_CENTER')}
                    variant="standard"
                    value={labClient.customer?.invoicing?.costCenter || ' '}
                    inputProps={{ readOnly: true }}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    label={translate('client-directory:GLN')}
                    variant="standard"
                    value={labClient.customer?.gln || ' '}
                    inputProps={{ readOnly: true }}
                    fullWidth
                />
            </Grid>

            <Grid item sm={12} xs={12}>
                <TextField
                    fullWidth
                    label={translate('client-directory:SUPPLIER_REFERENCE')}
                    {...formik.getFieldProps('supplierReference')}
                />
            </Grid>

            <Grid item sm={12} xs={12}>
                <AddressForm
                    address={labClient.invoiceSettings?.invoiceAddress || labClient.customer?.contact?.address}
                    onChange={handleAddressChange}
                    title={translate('client-directory:INVOICE_ADDRESS')}
                />
            </Grid>
        </Grid>
    );
};

export default InvoiceContentForm;
